import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { useTranslation } from 'next-i18next';
import { Modal } from '@components/common/Modal/Modal';
import { JobAddressMap } from './JobAddressMap';

/** Component props */
type ChangeAddress = (value: string) => void;
interface JobAddressMapModalProps {
  googleApiKey: string;
  address?: string;
  changeAddress?: ChangeAddress;
}

/**
 * @description Renders the subscriptions modal, with payment options, pricing and if
 * it's the case the 'not enough credits' and payment status possible steps.
 * The modal can be opened anywhere in the app using the **showUpgradeSubscriptionModal** function.
 * - `googleApiKey` - Google API key
 * - `address` - The job address
 * - `changeAddress` - Change address function
 */
export const JobAddressMapModal = NiceModal.create((props: JobAddressMapModalProps) => {
  // Destructure props
  const { googleApiKey, address, changeAddress } = props;

  // Use a hook to manage the modal state
  const modal = useModal();

  // Close modal
  const closeModal = () => {
    void modal.hide();
  };

  // Translation
  const { t } = useTranslation('common');

  // Render component
  // *****************************************
  return (
    <Modal
      isOpen={modal.visible}
      onClose={closeModal}
      size="md"
      header={t('company.detail.map_location')}
      body={(
        <JobAddressMap
          googleApiKey={googleApiKey}
          address={address}
          changeAddress={changeAddress}
          closeModal={closeModal}
        />
      )}
    />
  );
});

/** Function type */
type ShowJobAddressMapModal = (googleApiKey: string, address?: string, changeAddress?: ChangeAddress) => void;

/**
 * @description Function to show the job address map modal.
 * - `googleApiKey` - Google API key
 * - `address` - The job address
 * - `changeAddress` - Change address function
 *
 * @example
 * import { showJobAddressMapModal } from '@components/templates/job-add/edit/map/JobAddressMapModal.tsx';
 * // usage
 * <Button onClick={showJobAddressMapModal(googleApiKey, address)}>Buy neuro</Button>
 */
export const showJobAddressMapModal: ShowJobAddressMapModal = (
  googleApiKey,
  address,
  changeAddress,
) => {
  void NiceModal.show(JobAddressMapModal, {
    googleApiKey,
    address,
    changeAddress,
  });
};
